<template>
  <div>
    <!-- FIX: several top-level views (LandingPage.vue, DashboardPage.vue,
    NoAccess.vue) render their own nested <v-app>, and Vuetify's
    v-navigation-drawer/v-app-bar always position themselves against the
    real viewport (position: fixed) no matter how deep they're nested -
    so this banner, sitting in normal document flow in the OUTER App.vue,
    was getting rendered right over by those fixed shells instead of
    pushing them down. Making this stack itself position:fixed at the
    true viewport top (above Vuetify's own z-index scale) and measuring
    its real height into a CSS var - consumed by global rules below that
    nudge every nested shell's drawer/app-bar down by that amount - fixes
    it everywhere at once instead of patching each shell individually. -->
    <div ref="bannerStack" class="vozcare-release-banner-stack">
      <!-- "What's new" - shown once per release, until dismissed or viewed -->
      <v-slide-y-transition>
        <div v-if="showWhatsNew" class="vozcare-release-banner vozcare-release-banner--whats-new">
          <v-icon size="18" class="mr-2">mdi-gift-outline</v-icon>
          <span class="vozcare-release-banner__text">
            <strong>What's new</strong> ({{ formatDate(latestRelease?.date) }}) - {{ headlineNote }}
          </span>
          <!-- Outlined, not text: a plain "View details" text button was easy
          to miss, so users didn't realise the full notes were one click away. -->
          <v-btn
            size="small"
            variant="outlined"
            class="ml-3 flex-shrink-0"
            prepend-icon="mdi-text-box-outline"
            @click="openDialog"
          >
            View release notes
          </v-btn>
          <v-btn
            icon
            size="small"
            variant="text"
            class="ml-auto"
            title="Refresh"
            @click="reload"
          >
            <v-icon size="18">mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon size="small" variant="text" @click="dismissWhatsNew">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </div>
      </v-slide-y-transition>

      <!-- After Skip: a reminder that stays until the page is refreshed -->
      <div v-if="updateSkipped" class="vozcare-release-banner vozcare-release-banner--update">
        <v-icon size="18" class="mr-2">mdi-update</v-icon>
        <span class="vozcare-release-banner__text">
          A new version of the Admin Portal is available. Refresh when you have finished what you are doing.
        </span>
        <v-btn size="small" variant="outlined" class="ml-auto flex-shrink-0" prepend-icon="mdi-refresh" @click="reload">
          Refresh
        </v-btn>
      </div>
    </div>

    <!-- A newer build is live. Team leads and admins are often mid-task when
    a release goes out, so they can Skip (2026-10-28): the page is no longer
    reloaded for them while they work. A skipped update keeps a bar at the
    top and asks again after UPDATE_SKIP_MS, so no tab stays on old code for
    long. A page that has only just opened still reloads straight away. -->
    <v-dialog :model-value="updateAvailable" persistent max-width="440">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-update</v-icon>
          New version available
        </v-card-title>
        <v-card-text>
          A new version of the Admin Portal is available. Refresh now to load
          it, or skip and refresh when you have finished what you are doing.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="skipUpdate">Skip</v-btn>
          <v-btn color="primary" variant="flat" @click="reload">Refresh now</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogOpen" max-width="560">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-bell-outline</v-icon>
          Release notes
        </v-card-title>
        <v-card-text style="max-height: 60vh; overflow-y: auto">
          <div
            v-for="release in releaseNotes"
            :key="release.id"
            class="mb-5"
          >
            <div class="d-flex align-center mb-1">
              <span class="text-subtitle-1 font-weight-bold">
                {{ release.title || formatDate(release.date) }}
              </span>
              <span v-if="release.title" class="text-caption text-medium-emphasis ml-2">
                {{ formatDate(release.date) }}
              </span>
            </div>
            <ul class="pl-4">
              <li v-for="(note, i) in release.notes" :key="i" class="mb-1">
                {{ note }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <span v-if="buildLabel" class="text-caption text-medium-emphasis ml-2">
            {{ buildLabel }}
          </span>
          <v-spacer />
          <v-btn variant="text" @click="dialogOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  fetchBuildInfo,
  getReleaseNotes,
  getDismissedReleaseId,
  setDismissedReleaseId,
  isNewerThan,
  BUNDLE_COMMIT,
  markUpdatePending,
  tryAutoReloadTo,
} from "@/services/releaseNotesService";

// How often to re-check for a newer deployed build while the tab stays
// open. An SPA served from S3 never reloads on its own, so without this a
// tab left open across a deploy would silently keep running yesterday's
// code indefinitely. Also re-checked when the tab regains focus.
const UPDATE_POLL_INTERVAL_MS = 2 * 60 * 1000;
const FOCUS_CHECK_MIN_GAP_MS = 30 * 1000;
// After Skip, ask again this much later.
const UPDATE_SKIP_MS = 30 * 60 * 1000;

export default {
  name: "ReleaseNotesBanner",

  data: () => ({
    releaseNotes: [],
    latestRelease: null,
    showWhatsNew: false,
    updateAvailable: false,
    dialogOpen: false,
    loadedBuild: null,
    pollHandle: null,
    updateSkipped: false,
    skipHandle: null,
    lastUpdateCheckAt: 0,
  }),

  computed: {
    headlineNote() {
      return this.latestRelease?.notes?.[0] || "";
    },
    // Shown in the dialog footer - straight from the deployed
    // build-info.json, so it is always the real running build.
    buildLabel() {
      const b = this.loadedBuild;
      if (!b) return "";
      const built = b.builtAt ? ` · ${this.formatDate(b.builtAt)}` : "";
      return `Version ${b.version} (${b.commit})${built}`;
    },
  },

  async mounted() {
    // Measures the actual rendered height of whichever banner(s) are
    // showing and publishes it as a CSS var, so the global layout rules
    // in App.vue can push every nested shell's fixed drawer/app-bar down
    // by exactly that much - stays correct even if both banners stack, or
    // their content wraps to a second line on a narrow window.
    this.resizeObserver = new ResizeObserver(() => this.syncBannerOffset());
    this.resizeObserver.observe(this.$refs.bannerStack);

    await this.checkWhatsNew();
    await this.checkForUpdate({ initial: true });
    this.pollHandle = setInterval(this.checkForUpdate, UPDATE_POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", this.onFocusCheck);
    window.addEventListener("focus", this.onFocusCheck);
  },

  beforeUnmount() {
    if (this.pollHandle) clearInterval(this.pollHandle);
    if (this.skipHandle) clearTimeout(this.skipHandle);
    document.removeEventListener("visibilitychange", this.onFocusCheck);
    window.removeEventListener("focus", this.onFocusCheck);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    document.documentElement.style.removeProperty("--vozcare-banner-height");
    document.body.classList.remove("has-release-banner");
  },

  watch: {
    showWhatsNew() {
      this.$nextTick(this.syncBannerOffset);
    },
    updateAvailable() {
      this.$nextTick(this.syncBannerOffset);
    },
    updateSkipped() {
      this.$nextTick(this.syncBannerOffset);
    },
  },

  methods: {
    syncBannerOffset() {
      const height = this.$refs.bannerStack?.offsetHeight || 0;
      document.documentElement.style.setProperty(
        "--vozcare-banner-height",
        `${height}px`
      );
      document.body.classList.toggle("has-release-banner", height > 0);
    },
    formatDate(value) {
      if (!value) return "";
      // Date-only strings are parsed as UTC midnight; format in UTC too so
      // "2026-10-15" never shows as the 14th west of Greenwich.
      const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
      if (isNaN(d)) return value;
      return d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        ...(value.length === 10 ? { timeZone: "UTC" } : {}),
      });
    },
    async checkWhatsNew() {
      try {
        this.releaseNotes = getReleaseNotes();
        this.latestRelease = this.releaseNotes[0] || null;
        if (!this.latestRelease) return;
        const dismissed = await getDismissedReleaseId();
        this.showWhatsNew = isNewerThan(this.latestRelease.id, dismissed);
      } catch (error) {
        // Best-effort feature - should never block the rest of the app.
        console.warn("[ReleaseNotesBanner] could not check release notes:", error);
      }
    },
    onFocusCheck() {
      if (document.hidden) return;
      if (Date.now() - this.lastUpdateCheckAt < FOCUS_CHECK_MIN_GAP_MS) return;
      this.checkForUpdate();
    },
    // Compares the commit baked into THIS bundle with the live
    // build-info.json. Comparing against the bundle (not against whatever
    // build-info said when the tab opened) also catches a tab that booted
    // from a stale, browser-cached index.html.
    async checkForUpdate({ initial = false } = {}) {
      if (this.updateAvailable || this.updateSkipped) return;
      this.lastUpdateCheckAt = Date.now();
      let info;
      try {
        info = await fetchBuildInfo();
      } catch (error) {
        // A transient network blip shouldn't force a reload - just try
        // again next interval.
        if (initial) console.warn("[ReleaseNotesBanner] could not load build info:", error);
        return;
      }
      if (initial) this.loadedBuild = { ...info, commit: BUNDLE_COMMIT };
      if (BUNDLE_COMMIT === "dev" || !info.commit || info.commit === "dev") return;
      if (info.commit === BUNDLE_COMMIT) return;
      this.forceUpgrade(info.commit, { immediate: initial });
    },
    forceUpgrade(liveCommit, { immediate }) {
      markUpdatePending();
      // Only just loaded: nothing typed yet, reload straight away (once - see
      // tryAutoReloadTo). A tab in the background is no longer reloaded - it
      // may hold a half-filled form; it gets the dialog instead.
      if (immediate && tryAutoReloadTo(liveCommit)) return;
      this.updateAvailable = true;
    },
    skipUpdate() {
      this.updateAvailable = false;
      this.updateSkipped = true;
      if (this.skipHandle) clearTimeout(this.skipHandle);
      this.skipHandle = setTimeout(() => {
        this.updateSkipped = false;
        this.updateAvailable = true;
      }, UPDATE_SKIP_MS);
    },
    openDialog() {
      this.dialogOpen = true;
      this.dismissWhatsNew();
    },
    dismissWhatsNew() {
      this.showWhatsNew = false;
      if (this.latestRelease) {
        setDismissedReleaseId(this.latestRelease.id);
      }
    },
    reload() {
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.vozcare-release-banner-stack {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Above Vuetify's app-bar/drawer layer (~1005) so it's never hidden by
     a nested shell's fixed chrome, but below its overlay/dialog layer
     (~2400) so the "release notes" dialog still opens on top of it. */
  z-index: 2010;
}

.vozcare-release-banner {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 13px;
}

/* FIX: a flat rgb(var(--v-theme-primary)) fill read as dull/"muddy"
against this app's actual look, which always uses a two-tone gradient for
anything primary-branded (see .build-btn-light/.build-btn-dark on the
Dashboard's own CTA buttons: #4f8ef7->#00d4ff light, #0d9488->#00f2fe
dark - the same pair used here, kept in sync with light/dark via the
.v-theme--light/.v-theme--dark class Vuetify puts on the app root). */
.v-theme--light .vozcare-release-banner--whats-new {
  background: linear-gradient(to left, #4f8ef7, #00d4ff);
  color: #ffffff;
}
.v-theme--dark .vozcare-release-banner--whats-new {
  background: linear-gradient(to left, #0d9488, #00f2fe);
  color: #ffffff;
}

.vozcare-release-banner--update {
  /* Semantic "action needed" color, not the brand accent - kept distinct
  from "what's new" on purpose, but still theme-sourced (Vuetify ships a
  sensible warning/on-warning pair by default even though this app's
  theme config doesn't define one explicitly), and given the same
  two-tone gradient treatment as the brand color above for consistency. */
  background: linear-gradient(
    to left,
    rgb(var(--v-theme-warning)),
    color-mix(in srgb, rgb(var(--v-theme-warning)) 60%, white)
  );
  color: rgb(var(--v-theme-on-warning));
}

.vozcare-release-banner__text {
  /* shrink (with an ellipsis) so the buttons beside it stay on screen */
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
